import { lang } from "src/language/Strings";
import { Button } from "./Buttons/Button";
import { drawnSignatureType, SignatureInput } from "./SignatureInput";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { a } from "vitest/dist/chunks/suite.qtkXWc6R";

export function SigningModal({
  documentTitle,
  onSave,
  setIsActive,
}: {
  documentTitle?: string;
  onSave: (image: string) => void;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { signingSessionId } = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasImage, setCanvasImage] = useState<any>();
  const [redrawText, setRedrawText] = useState<(() => void) | null>(null);
  const [clearIndex, setClearIndex] = useState<number>(0);
  const onClear = useCallback(() => {
    setDrawnCoordinates([]);
    setTempImage(undefined);
    setCanvasImage(undefined);
    setSignedTextArea([0, 0, 0, 0]);
    setRedrawText(null);
  }, []);
  const [drawnCoordinates, setDrawnCoordinates] = useState<drawnSignatureType>(
    []
  );
  const [tempImage, setTempImage] = useState<string | undefined>(undefined);
  const [signedTextArea, setSignedTextArea] = useState<
    [number, number, number, number]
  >([0, 0, 0, 0]);

  const getSignedImageDataUrl = useCallback(() => {
    if (!drawnCoordinates?.length) return;
    function getLineWidth(p0, p1) {
      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      return Math.max(0.8, 1.5 - speed * 0.1); // justerbar
    }

    if (!drawnCoordinates?.length) return;
    // 2) Skala upp drawnCoordinates så att det ser mycket mer runt ut. Vi kan skala upp med 4.

    const scaledCoordinates = drawnCoordinates.map((stroke) =>
      stroke.map(({ x, y }) => ({
        x: x * 4,
        y: y * 4,
      }))
    );
    // 2) Beräkna min/max per axel
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const stroke of scaledCoordinates) {
      for (const { x, y } of stroke) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    // 3) Skapa canvas
    const PAD = 2;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(maxX - minX) + PAD * 2);
    canvas.height = Math.max(1, Math.ceil(maxY - minY) + PAD * 2);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.shadowColor = "rgba(0, 0, 128, 0.3)";
    ctx.shadowBlur = 1.5;
    // 4) Normalisera punkter så de börjar vid (0,0)
    let norm = scaledCoordinates.map((stroke) =>
      stroke.map(({ x, y }) => ({
        x: x - minX,
        y: y - minY,
      }))
    );
    // 5) Stil
    ctx.lineWidth = 1;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "gray";

    // 6) Rita – börja med moveTo, sedan lineTo
    //Can we make sure the drawn lines are smooth and does not have hard edges?

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.beginPath();
    for (const stroke of norm) {
      ctx.moveTo(stroke[0].x + PAD, stroke[0].y + PAD);
      for (let i = 1; i < stroke.length - 1; i++) {
        const p0 = stroke[i];
        const p1 = stroke[i + 1];
        const cx = p0.x + PAD; // control = nuvarande punkt
        const cy = p0.y + PAD;
        const mx = (p0.x + p1.x) / 2 + PAD; // mid point till nästa
        const my = (p0.y + p1.y) / 2 + PAD;
        ctx.lineWidth = getLineWidth(p0, p1);
        ctx.bezierCurveTo(cx, cy, mx, my, p1.x + PAD, p1.y + PAD);
      }
      ctx.stroke();
    }

    const imageUrl = canvas.toDataURL("image/png");
    setTempImage(imageUrl);
    return imageUrl;
  }, [drawnCoordinates]);
  useEffect(() => {
    getSignedImageDataUrl();
  }, [drawnCoordinates]);
  return (
    <div className="inset-0 flex justify-center align-middle w-full h-screen bg-White items-center flex-col">
      <div className="absolute w-full flex flex-col items-center">
        <h1>{documentTitle}</h1>
        <SignatureInput
          setDrawnCoordinates={setDrawnCoordinates}
          setSignedTextArea={setSignedTextArea}
          className="h-44 border-2 border-PrimaryGreen rounded-lg mb-4"
          canvasRef={canvasRef}
          onClear={onClear}
          clearIndex={clearIndex}
        />
        <div>
          <Button
            title={lang.clear}
            onClick={() => {
              setDrawnCoordinates([]);
              setTempImage(undefined);
              setCanvasImage(undefined);
              setSignedTextArea([0, 0, 0, 0]);
              setClearIndex((prev) => prev + 1);
            }}
          />
          <Button
            title={lang.save}
            onClick={() => {
              const image = getSignedImageDataUrl();

              setCanvasImage(image);
              if (image) {
                onSave(image);
              }
              setIsActive(false);
            }}
          />
        </div>
      </div>
    </div>
  );
}
